import { create } from 'zustand';
import { User, Analysis } from '../types';
import { 
  registerUser as firebaseRegister,
  loginUser as firebaseLogin,
  logoutUser as firebaseLogout,
  sendPasswordResetEmail,
  confirmPasswordReset
} from '../services/firebase/auth';
import {
  saveAnalysis,
  getUserAnalyses,
  deleteAnalysis as deleteFirebaseAnalysis
} from '../services/firebase/database';

interface Store {
  user: User | null;
  analyses: Analysis[];
  isLoading: boolean;
  isInitializing: boolean;
  error: string | null;
  setUser: (user: User | null) => void;
  setIsInitializing: (value: boolean) => void;
  addAnalysis: (analysis: Omit<Analysis, 'id'>) => Promise<void>;
  deleteAnalysis: (id: string) => Promise<void>;
  loadUserAnalyses: () => Promise<void>;
  registerUser: (data: { name: string; email: string; password: string }) => Promise<User>;
  loginUser: (email: string, password: string) => Promise<User>;
  logoutUser: () => Promise<void>;
  requestPasswordReset: (email: string) => Promise<void>;
  confirmPasswordReset: (token: string, newPassword: string) => Promise<void>; // Nova função
  clearError: () => void;
}

export const useStore = create<Store>((set, get) => ({
  user: null,
  analyses: [],
  isLoading: false,
  isInitializing: true,
  error: null,
  
  setUser: (user) => {
    set({ user });
    if (user) {
      get().loadUserAnalyses().catch(console.error);
    }
  },

  setIsInitializing: (value) => set({ isInitializing: value }),
  
  addAnalysis: async (analysisData) => {
    const { user } = get();
    if (!user) throw new Error('User not authenticated');
    
    try {
      set({ isLoading: true, error: null });
      const id = await saveAnalysis({ ...analysisData, userId: user.id });
      set((state) => ({
        analyses: [{ id, ...analysisData, userId: user.id }, ...state.analyses]
      }));
    } catch (error: any) {
      set({ error: error.message });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  deleteAnalysis: async (id) => {
    try {
      set({ isLoading: true, error: null });
      await deleteFirebaseAnalysis(id);
      set((state) => ({
        analyses: state.analyses.filter((a) => a.id !== id)
      }));
    } catch (error: any) {
      set({ error: error.message });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },
  
  loadUserAnalyses: async () => {
    const { user } = get();
    if (!user) return;

    try {
      set({ isLoading: true, error: null });
      const analyses = await getUserAnalyses(user.id);
      set({ analyses });
    } catch (error: any) {
      console.error('Error loading analyses:', error);
      set({ analyses: [] });
    } finally {
      set({ isLoading: false });
    }
  },
  
  registerUser: async ({ name, email, password }) => {
    try {
      set({ isLoading: true, error: null });
      return await firebaseRegister(email, password, name);
    } catch (error: any) {
      set({ error: error.message });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },
  
  loginUser: async (email, password) => {
    try {
      set({ isLoading: true, error: null });
      return await firebaseLogin(email, password);
    } catch (error: any) {
      set({ error: error.message });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },
  
  logoutUser: async () => {
    try {
      set({ isLoading: true, error: null });
      await firebaseLogout();
      set({ user: null, analyses: [] });
    } catch (error: any) {
      set({ error: error.message });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },
  
  requestPasswordReset: async (email) => {
    try {
      set({ isLoading: true, error: null });
      await sendPasswordResetEmail(email);
    } catch (error: any) {
      set({ error: error.message });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  confirmPasswordReset: async (token, newPassword) => {
    try {
      set({ isLoading: true, error: null });
      await confirmPasswordReset(token, newPassword);
    } catch (error: any) {
      set({ error: error.message });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  clearError: () => set({ error: null })
}));
